<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

import CampaignPopup from './CampaignPopup.vue';
import ConfirmDeleteUnofficialCampaignDialog from './ConfirmDeleteUnofficialCampaignDialog.vue';
import {
  listUnofficialCampaigns,
  normalizeUnofficialCampaign,
} from '../assets/unofficialCampaigns.service.js';

const { t } = useI18n();

const searchQuery = ref('');
const campaigns = ref([]);
const isLoading = ref(false);
const listError = ref('');
const selectedCampaign = ref(null);

const campaignPopupRef = ref(null);
const confirmDeleteDialogRef = ref(null);

const filteredCampaigns = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();

  if (!query) return campaigns.value;

  return campaigns.value.filter(campaign =>
    String(campaign?.nome || '')
      .toLowerCase()
      .includes(query)
  );
});

const hasSearchQuery = computed(() => searchQuery.value.trim().length > 0);

const emptyStateTitle = computed(() =>
  hasSearchQuery.value
    ? t('CAMPAIGN.UNOFFICIAL.LIST.EMPTY_FILTERED_TITLE')
    : t('CAMPAIGN.UNOFFICIAL.LIST.EMPTY_TITLE')
);

const emptyStateSubtitle = computed(() =>
  hasSearchQuery.value
    ? t('CAMPAIGN.UNOFFICIAL.LIST.EMPTY_FILTERED_SUBTITLE')
    : t('CAMPAIGN.UNOFFICIAL.LIST.EMPTY_SUBTITLE')
);

const alertClass = computed(() =>
  listError.value
    ? 'border-n-ruby-6 bg-n-ruby-2 text-n-ruby-11'
    : 'border-n-weak bg-n-solid-2 text-n-slate-11'
);

const formatDateTime = value => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const formatPeriod = campaign => {
  return `${formatDateTime(campaign?.dt_inicio)} - ${formatDateTime(
    campaign?.dt_fim
  )}`;
};

const formatCampaignId = campaignId => `#${campaignId ?? '-'}`;

const loadCampaigns = async () => {
  isLoading.value = true;
  listError.value = '';

  try {
    const payload = await listUnofficialCampaigns();
    let rows = [];

    if (Array.isArray(payload)) {
      rows = payload;
    } else if (Array.isArray(payload?.data)) {
      rows = payload.data;
    }

    campaigns.value = rows.map(normalizeUnofficialCampaign);
  } catch (error) {
    listError.value =
      error?.response?.data?.message ||
      error?.message ||
      t('CAMPAIGN.UNOFFICIAL.LIST.ERROR_MESSAGE');
  } finally {
    isLoading.value = false;
  }
};

const openCreatePopup = () => {
  campaignPopupRef.value?.open();
};

const openEditPopup = campaign => {
  campaignPopupRef.value?.open({
    mode: 'edit',
    campaignId: campaign.id,
  });
};

const openDeleteDialog = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteDialogRef.value?.dialogRef.open();
};

onMounted(() => {
  loadCampaigns();
});
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.UNOFFICIAL.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.UNOFFICIAL.NEW_CAMPAIGN')"
    @click="openCreatePopup"
  >
    <template #action>
      <CampaignPopup ref="campaignPopupRef" @saved="loadCampaigns" />
    </template>

    <div class="flex flex-col gap-4 pb-6">
      <section
        class="overflow-hidden border rounded-xl border-n-weak bg-n-solid-1"
      >
        <div class="flex flex-col gap-4 p-4 sm:p-5">
          <div class="space-y-1">
            <h2 class="text-base font-medium text-n-slate-12">
              {{ t('CAMPAIGN.UNOFFICIAL.LIST.SECTION_TITLE') }}
            </h2>
            <p class="text-sm text-n-slate-11">
              {{ t('CAMPAIGN.UNOFFICIAL.LIST.SECTION_SUBTITLE') }}
            </p>
          </div>

          <div class="flex flex-col gap-3 lg:flex-row lg:items-end">
            <Input
              v-model="searchQuery"
              type="search"
              :label="t('CAMPAIGN.UNOFFICIAL.LIST.SEARCH_LABEL')"
              :placeholder="t('CAMPAIGN.UNOFFICIAL.LIST.SEARCH_PLACEHOLDER')"
              class="flex-1"
            />

            <Button
              :label="t('CAMPAIGN.UNOFFICIAL.LIST.REFRESH')"
              variant="faded"
              color="slate"
              size="md"
              :is-loading="isLoading"
              class="w-full lg:w-auto"
              @click="loadCampaigns"
            />
          </div>

          <div
            v-if="listError"
            class="flex flex-col gap-3 p-3 border rounded-lg sm:flex-row sm:items-center sm:justify-between"
            :class="alertClass"
          >
            <p class="mb-0 text-sm">
              {{ listError }}
            </p>

            <Button
              :label="t('CAMPAIGN.UNOFFICIAL.LIST.RETRY')"
              variant="link"
              color="ruby"
              size="sm"
              class="justify-start sm:justify-end"
              @click="loadCampaigns"
            />
          </div>
        </div>

        <div
          v-if="isLoading"
          class="flex items-center justify-center px-4 py-16 border-t border-n-weak text-n-slate-11"
        >
          <Spinner />
        </div>

        <template v-else-if="filteredCampaigns.length">
          <div class="hidden border-t border-n-weak md:block">
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-n-weak">
                <thead class="bg-n-solid-2">
                  <tr>
                    <th
                      class="px-4 py-3 text-xs font-medium tracking-wide uppercase text-left text-n-slate-11"
                    >
                      {{ t('CAMPAIGN.UNOFFICIAL.LIST.COLUMNS.ID') }}
                    </th>
                    <th
                      class="px-4 py-3 text-xs font-medium tracking-wide uppercase text-left text-n-slate-11"
                    >
                      {{ t('CAMPAIGN.UNOFFICIAL.LIST.COLUMNS.NAME') }}
                    </th>
                    <th
                      class="px-4 py-3 text-xs font-medium tracking-wide uppercase text-left text-n-slate-11"
                    >
                      {{ t('CAMPAIGN.UNOFFICIAL.LIST.COLUMNS.SCHEDULE') }}
                    </th>
                    <th
                      class="px-4 py-3 text-xs font-medium tracking-wide uppercase text-left text-n-slate-11"
                    >
                      {{ t('CAMPAIGN.UNOFFICIAL.LIST.COLUMNS.PERIOD') }}
                    </th>
                    <th
                      class="px-4 py-3 text-xs font-medium tracking-wide uppercase text-right text-n-slate-11"
                    >
                      {{ t('CAMPAIGN.UNOFFICIAL.LIST.COLUMNS.ACTIONS') }}
                    </th>
                  </tr>
                </thead>

                <tbody class="divide-y divide-n-weak">
                  <tr
                    v-for="campaign in filteredCampaigns"
                    :key="campaign.id"
                    class="bg-n-solid-1"
                  >
                    <td class="px-4 py-4 text-sm text-n-slate-12">
                      {{ campaign.id ?? '-' }}
                    </td>
                    <td class="px-4 py-4 text-sm font-medium text-n-slate-12">
                      {{ campaign.nome ?? '-' }}
                    </td>
                    <td class="px-4 py-4 text-sm text-n-slate-12">
                      {{ campaign.horario_disparo ?? '-' }}
                    </td>
                    <td class="px-4 py-4 text-sm text-n-slate-11">
                      {{ formatPeriod(campaign) }}
                    </td>
                    <td class="px-4 py-4">
                      <div class="flex justify-end gap-2">
                        <Button
                          :label="t('CAMPAIGN.UNOFFICIAL.LIST.ACTIONS.EDIT')"
                          variant="faded"
                          color="slate"
                          size="sm"
                          @click="openEditPopup(campaign)"
                        />
                        <Button
                          :label="t('CAMPAIGN.UNOFFICIAL.LIST.ACTIONS.DELETE')"
                          variant="faded"
                          color="ruby"
                          size="sm"
                          @click="openDeleteDialog(campaign)"
                        />
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="grid gap-3 p-4 border-t border-n-weak md:hidden">
            <article
              v-for="campaign in filteredCampaigns"
              :key="campaign.id"
              class="flex flex-col gap-4 p-4 border rounded-xl border-n-weak bg-n-solid-2"
            >
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <p class="mb-1 text-xs font-medium uppercase text-n-slate-11">
                    {{ t('CAMPAIGN.UNOFFICIAL.LIST.COLUMNS.ID') }}
                    {{ formatCampaignId(campaign.id) }}
                  </p>
                  <h3 class="text-base font-medium break-words text-n-slate-12">
                    {{ campaign.nome ?? '-' }}
                  </h3>
                </div>
              </div>

              <dl class="grid gap-3 sm:grid-cols-2">
                <div class="space-y-1">
                  <dt class="text-xs font-medium uppercase text-n-slate-11">
                    {{ t('CAMPAIGN.UNOFFICIAL.LIST.COLUMNS.SCHEDULE') }}
                  </dt>
                  <dd class="m-0 text-sm text-n-slate-12">
                    {{ campaign.horario_disparo ?? '-' }}
                  </dd>
                </div>

                <div class="space-y-1">
                  <dt class="text-xs font-medium uppercase text-n-slate-11">
                    {{ t('CAMPAIGN.UNOFFICIAL.LIST.COLUMNS.PERIOD') }}
                  </dt>
                  <dd class="m-0 text-sm break-words text-n-slate-11">
                    {{ formatPeriod(campaign) }}
                  </dd>
                </div>
              </dl>

              <div class="flex flex-col gap-2 sm:flex-row">
                <Button
                  :label="t('CAMPAIGN.UNOFFICIAL.LIST.ACTIONS.EDIT')"
                  variant="faded"
                  color="slate"
                  class="w-full"
                  @click="openEditPopup(campaign)"
                />
                <Button
                  :label="t('CAMPAIGN.UNOFFICIAL.LIST.ACTIONS.DELETE')"
                  variant="faded"
                  color="ruby"
                  class="w-full"
                  @click="openDeleteDialog(campaign)"
                />
              </div>
            </article>
          </div>

          <footer
            class="flex items-center justify-between px-4 py-4 border-t border-n-weak bg-n-solid-2"
          >
            <p class="mb-0 text-xs text-n-slate-11">
              {{
                t('CAMPAIGN.UNOFFICIAL.LIST.SHOWING_COUNT', {
                  count: filteredCampaigns.length,
                })
              }}
            </p>
          </footer>
        </template>

        <div
          v-else
          class="flex flex-col items-center gap-3 px-6 py-16 text-center border-t border-n-weak"
        >
          <h3 class="text-lg font-medium text-n-slate-12">
            {{ emptyStateTitle }}
          </h3>
          <p class="max-w-md mb-0 text-sm text-n-slate-11">
            {{ emptyStateSubtitle }}
          </p>
        </div>
      </section>
    </div>

    <ConfirmDeleteUnofficialCampaignDialog
      ref="confirmDeleteDialogRef"
      :selected-campaign="selectedCampaign"
      @deleted="loadCampaigns"
    />
  </CampaignLayout>
</template>
